import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightbulb, ArrowLeft, Loader2, Copy, CheckCircle2, BookOpen, Zap, Layout } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SpEdKitLogo from '../components/SpEdKitLogo';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UDLStrategies = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    learning_barrier: '',
    grade_level: '',
    subject_area: '',
    current_supports: '',
    student_strengths: '',
    learning_environment: 'general education classroom'
  });

  const gradeOptions = [
    'ECE (Early Childhood)', 'Kindergarten', 
    '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade',
    '6th Grade', '7th Grade', '8th Grade', 
    '9th Grade', '10th Grade', '11th Grade', '12th Grade',
    'YA (Young Adult 18-22)'
  ];

  const subjectOptions = [
    'Reading/Language Arts', 'Phonics', 'Fluency', 'Reading Comprehension',
    'Math Calculation', 'Math Applications', 'Written Language', 'Science', 'Social Studies',
    'Fine Motor', 'Gross Motor', 'Physical Education', 'Art', 'Music', 
    'Social Skills', 'Executive Functioning', 'Activities of Daily Living',
    'Pre-Vocational Skills', 'Adult Transition', 'General/Multiple Subjects'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.learning_barrier.trim() || !formData.grade_level) {
      toast.error('Please fill in the learning barrier and grade level');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(`${API}/suggest-udl-strategies`, formData, {
        withCredentials: true
      });
      setResult(response.data);
      toast.success('UDL strategies generated successfully!');
    } catch (error) {
      console.error('Error generating strategies:', error);
      toast.error('Failed to generate strategies. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!result) return;
    
    const text = formatResultAsText(result);
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatResultAsText = (data) => {
    const strategies = data.udl_strategies;
    let text = `UDL STRATEGIES\n`;
    text += `Learning Barrier: ${data.learning_barrier}\n`;
    text += `Grade Level: ${data.grade_level}\n\n`;
    text += `ANALYSIS\n${strategies.barrier_analysis}\n\n`;
    text += `FOCUS: ${strategies.udl_principle_focus}\n\n`;
    text += `STRATEGIES:\n`;
    strategies.strategies?.forEach((s, i) => {
      text += `\n${i + 1}. ${s.name} (${s.category})\n`;
      text += `   ${s.description}\n`;
      text += `   How it helps: ${s.how_it_helps}\n`;
      text += `   Implementation:\n`;
      s.implementation?.forEach(step => {
        text += `   - ${step}\n`;
      });
    });
    return text;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'engagement': return <Zap className="w-4 h-4" />;
      case 'representation': return <BookOpen className="w-4 h-4" />;
      case 'action_expression': return <Layout className="w-4 h-4" />;
      default: return <Lightbulb className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'engagement': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'representation': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'action_expression': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="udl-strategies-page">
      {/* Header */}
      <header className="w-full px-6 py-4 bg-white shadow-soft">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
            <SpEdKitLogo className="w-10 h-10" textClassName="text-2xl" />
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-text-secondary hover:text-primary"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="px-6 py-12 max-w-5xl mx-auto">
        <div className="mb-8 animate-fade-in-up">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
              <Lightbulb className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl text-text-primary">
                UDL Strategy Hunter
              </h1>
              <p className="font-body text-text-secondary">
                Find evidence-based accommodations and UDL strategies
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="bg-white rounded-2xl p-8 shadow-card animate-fade-in-up">
            <h2 className="font-heading font-semibold text-xl text-text-primary mb-6">
              Describe the Learning Challenge
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="learning_barrier" className="text-text-primary font-medium">
                  Learning Barrier *
                </Label>
                <Textarea
                  id="learning_barrier"
                  placeholder="e.g., Student struggles to maintain attention during whole-class instruction, frequently misses verbal directions, and has difficulty completing multi-step tasks independently."
                  value={formData.learning_barrier}
                  onChange={(e) => setFormData({ ...formData, learning_barrier: e.target.value })}
                  className="mt-2 min-h-[120px]"
                  data-testid="learning-barrier-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="grade_level" className="text-text-primary font-medium">
                    Grade Level *
                  </Label>
                  <Select
                    value={formData.grade_level}
                    onValueChange={(value) => setFormData({ ...formData, grade_level: value })}
                  >
                    <SelectTrigger className="mt-2" data-testid="grade-level-select">
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {gradeOptions.map((grade) => (
                        <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="subject_area" className="text-text-primary font-medium">
                    Subject Area
                  </Label>
                  <Select
                    value={formData.subject_area}
                    onValueChange={(value) => setFormData({ ...formData, subject_area: value })}
                  >
                    <SelectTrigger className="mt-2" data-testid="subject-area-select">
                      <SelectValue placeholder="Select subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjectOptions.map((subject) => (
                        <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="student_strengths" className="text-text-primary font-medium">
                  Student Strengths (Optional)
                </Label>
                <Textarea
                  id="student_strengths"
                  placeholder="e.g., Strong visual learner, creative, works well with peers, passionate about science..."
                  value={formData.student_strengths}
                  onChange={(e) => setFormData({ ...formData, student_strengths: e.target.value })}
                  className="mt-2"
                  data-testid="student-strengths-input"
                />
              </div>

              <div>
                <Label htmlFor="current_supports" className="text-text-primary font-medium">
                  Current Supports Being Used (Optional)
                </Label>
                <Input
                  id="current_supports"
                  placeholder="e.g., Preferential seating, visual schedule, fidget tools..."
                  value={formData.current_supports}
                  onChange={(e) => setFormData({ ...formData, current_supports: e.target.value })}
                  className="mt-2"
                  data-testid="current-supports-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-accent text-white hover:bg-accent/90 rounded-full py-3"
                data-testid="generate-strategies-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Finding Strategies...
                  </>
                ) : (
                  <>
                    <Lightbulb className="w-5 h-5 mr-2" />
                    Find UDL Strategies
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Results */}
          <div className="animate-fade-in-up animation-delay-200">
            {result ? (
              <div className="bg-white rounded-2xl p-8 shadow-card">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="font-heading font-semibold text-xl text-text-primary">
                    Recommended Strategies
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyToClipboard}
                    className="text-text-secondary"
                    data-testid="copy-strategies-btn"
                  >
                    {copied ? (
                      <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4 mr-2" />
                    )}
                    {copied ? 'Copied!' : 'Copy All'}
                  </Button>
                </div>

                {/* Analysis */}
                <div className="bg-accent/5 rounded-xl p-4 mb-6">
                  <h3 className="font-heading font-medium text-accent mb-2">Barrier Analysis</h3>
                  <p className="font-body text-text-secondary text-sm">
                    {result.udl_strategies?.barrier_analysis}
                  </p>
                  <p className="font-body text-accent text-sm mt-2 font-medium">
                    Focus: {result.udl_strategies?.udl_principle_focus}
                  </p>
                </div>

                {/* Strategies */}
                <div className="space-y-4">
                  {result.udl_strategies?.strategies?.map((strategy, index) => (
                    <div 
                      key={index}
                      className="border border-secondary rounded-xl p-4"
                      data-testid={`strategy-card-${index}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-heading font-semibold text-text-primary">
                          {strategy.name}
                        </h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getCategoryColor(strategy.category)}`}>
                          {getCategoryIcon(strategy.category)}
                          {strategy.category?.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="font-body text-text-secondary text-sm mb-3">
                        {strategy.description}
                      </p>
                      <div className="bg-primary/5 rounded-lg p-3 mb-3">
                        <p className="font-body text-primary text-sm">
                          <strong>How it helps:</strong> {strategy.how_it_helps}
                        </p>
                      </div>
                      <div>
                        <p className="font-body text-text-primary text-sm font-medium mb-2">Implementation:</p>
                        <ul className="space-y-1">
                          {strategy.implementation?.map((step, i) => (
                            <li key={i} className="font-body text-text-secondary text-sm flex items-start gap-2">
                              <span className="w-5 h-5 bg-secondary rounded-full flex items-center justify-center flex-shrink-0 text-xs text-primary font-medium">
                                {i + 1}
                              </span>
                              {step}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {strategy.materials_needed && (
                        <p className="font-body text-text-muted text-xs mt-3">
                          Materials: {strategy.materials_needed}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Additional Info */}
                {result.udl_strategies?.technology_supports?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-secondary">
                    <h3 className="font-heading font-medium text-text-primary mb-3">Technology Supports</h3>
                    <ul className="space-y-2">
                      {result.udl_strategies.technology_supports.map((tech, i) => (
                        <li key={i} className="font-body text-text-secondary text-sm flex items-center gap-2">
                          <div className="w-2 h-2 bg-accent rounded-full"></div>
                          {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.udl_strategies?.progress_monitoring && (
                  <div className="mt-6 bg-green-50 rounded-xl p-4">
                    <h3 className="font-heading font-medium text-green-700 mb-2">Progress Monitoring</h3>
                    <p className="font-body text-green-600 text-sm">
                      {result.udl_strategies.progress_monitoring}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-secondary/30 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lightbulb className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-heading font-semibold text-text-primary mb-2">
                  Ready to Find Strategies
                </h3>
                <p className="font-body text-text-secondary text-sm max-w-sm mx-auto">
                  Describe a learning challenge and we'll suggest evidence-based UDL strategies and accommodations.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UDLStrategies;
